// import { useState } from "react";
// import { API_BASE_URL, API_ENDPOINTS } from "../config/api";
// import Toast from "../utils/toast";
// import { useLoader } from "../context/LoaderContext";

// /** Don't redirect on 401 when this is the login/register request (user is already on login page). */
// const isLoginEndpoint = (endpoint) =>
//   endpoint === API_ENDPOINTS.LOGIN || (typeof endpoint === "string" && endpoint.includes("admin_login"));

// export const useApi = (endpoint, options = {}) => {
//   const [data, setData] = useState(null);
//   const [error, setError] = useState(null);
//   const { setLoading } = useLoader();

//   const execute = async (customOptions = {}) => {
//     setLoading(true);
//     setError(null);

//     try {
//       const token = localStorage.getItem("token");

//       const config = {
//         ...options,
//         ...customOptions,
//         headers: {
//           "Content-Type": "application/json",
//           ...(token && { Authorization: `Bearer ${token}` }),
//         },
//       };

//       if (config.body && typeof config.body === "object") {
//         config.body = JSON.stringify(config.body);
//       }

//       const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

//       // 🔐 TOKEN EXPIRED – redirect only for protected APIs, NOT for login request
//       if (response.status === 401 || response.status === 403) {
//         if (!isLoginEndpoint(endpoint)) {
//           localStorage.removeItem("token");
//           Toast.error("Session expired. Please login again.");
//           window.location.href = "/login";
//         }
//         const errBody = await response.json().catch(() => ({}));
//         throw new Error(errBody?.message || errBody?.detail || "Unauthorized");
//       }

//       if (!response.ok) {
//         const err = await response.json().catch(() => ({}));
//         throw new Error(err?.message || err?.detail || "Request failed");
//       }

//       const result = await response.json();
//       setData(result);
//       return result;
//     } catch (err) {
//       setError(err.message);
//       if (err.message !== "Unauthorized" && !isLoginEndpoint(endpoint)) {
//         Toast.error(err.message);
//       }
//       throw err;
//     } finally {
//       setLoading(false);
//     }
//   };

//   return { data, error, execute };
// };

// export const useGet = (endpoint) => {
//   return useApi(endpoint, { method: "GET" });
// };

// export const usePost = (endpoint) => {
//   const { execute, ...rest } = useApi(endpoint, { method: "POST" });
//   const post = (body) => execute({ body });
//   return { ...rest, post };
// };

// export const usePut = (endpoint) => {
//   const { execute, ...rest } = useApi(endpoint, { method: "PUT" });
//   const put = (body) => execute({ body });
//   return { ...rest, put };
// };

// export const useDelete = (endpoint) => {
//   const { execute, ...rest } = useApi(endpoint, { method: "DELETE" });
//   const del = () => execute();
//   return { ...rest, delete: del };
// };

// /**
//  * Hook for dynamic endpoint calls (GET, PUT, DELETE with ID)
//  * @param {string} method - HTTP method (GET, PUT, DELETE, etc.)
//  * @returns {object} Object with execute function that accepts endpoint
//  */
// export const useDynamicApi = (method = "GET") => {
//   const [data, setData] = useState(null);
//   const [error, setError] = useState(null);
//   const { setLoading } = useLoader();

//   const execute = async (endpoint, bodyData = null) => {
//     setLoading(true);
//     setError(null);

//     try {
//       const token = localStorage.getItem("token");

//       const config = {
//         method,
//         headers: {
//           "Content-Type": "application/json",
//           ...(token && { Authorization: `Bearer ${token}` }),
//         },
//       };

//       if (bodyData) {
//         config.body = JSON.stringify(bodyData);
//       }

//       const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

//       // 🔐 TOKEN EXPIRED – redirect only for protected APIs, NOT for login request
//       if (response.status === 401 || response.status === 403) {
//         if (!isLoginEndpoint(endpoint)) {
//           localStorage.removeItem("token");
//           Toast.error("Session expired. Please login again.");
//           window.location.href = "/login";
//         }
//         const errBody = await response.json().catch(() => ({}));
//         throw new Error(errBody?.message || errBody?.detail || "Unauthorized");
//       }

//       if (!response.ok) {
//         const err = await response.json().catch(() => ({}));
//         throw new Error(err?.message || err?.detail || "Request failed");
//       }

//       const result = await response.json();
//       setData(result);
//       return result;
//     } catch (err) {
//       setError(err.message);
//       if (err.message !== "Unauthorized" && !isLoginEndpoint(endpoint)) {
//         Toast.error(err.message);
//       }
//       throw err;
//     } finally {
//       setLoading(false);
//     }
//   };

//   return { data, error, execute };
// };

import { useState } from "react";
import { API_BASE_URL, API_ENDPOINTS } from "../config/api";
import Toast from "../utils/toast";
import { useLoader } from "../context/LoaderContext";

/** Don't redirect on 401 when this is the login/register request (user is already on login page). */
const isLoginEndpoint = (endpoint) =>
  endpoint === API_ENDPOINTS.LOGIN ||
  (typeof endpoint === "string" && endpoint.includes("admin_login"));

export const useApi = (endpoint, options = {}) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { setLoading } = useLoader();

  const execute = async (customOptions = {}) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");

      const config = {
        ...options,
        ...customOptions,
        headers: {
          "Content-Type": "application/json",
          // Don't send expired tokens on login requests
          ...(token && !isLoginEndpoint(endpoint) && { Authorization: `Bearer ${token}` }),
        },
      };

      if (config.body && typeof config.body === "object") {
        config.body = JSON.stringify(config.body);
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

      // 🔐 TOKEN EXPIRED – redirect only for protected APIs, NOT for login request
      if (response.status === 401 || response.status === 403) {
        if (!isLoginEndpoint(endpoint)) {
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          // Only redirect if not already on login page (prevents Safari reload loop)
          if (!window.location.pathname.includes("/login")) {
            Toast.error("Session expired. Please login again.");
            window.location.href = "/login";
          }
        }
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody?.message || errBody?.detail || "Unauthorized");
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || err?.detail || "Request failed");
      }

      const result = await response.json();
      setData(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { data, error, execute };
};

export const useGet = (endpoint) => {
  return useApi(endpoint, { method: "GET" });
};

export const usePost = (endpoint) => {
  const { execute, ...rest } = useApi(endpoint, { method: "POST" });
  const post = (body) => execute({ body });
  return { ...rest, post };
};

export const usePut = (endpoint) => {
  const { execute, ...rest } = useApi(endpoint, { method: "PUT" });
  const put = (body) => execute({ body });
  return { ...rest, put };
};

export const useDelete = (endpoint) => {
  const { execute, ...rest } = useApi(endpoint, { method: "DELETE" });
  const del = () => execute();
  return { ...rest, delete: del };
};

/**
 * Hook for dynamic endpoint calls (GET, PUT, DELETE with ID)
 * @param {string} method - HTTP method (GET, PUT, DELETE, etc.)
 * @returns {object} Object with execute function that accepts endpoint
 */
export const useDynamicApi = (method = "GET") => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { setLoading } = useLoader();

  const execute = async (endpoint, bodyData = null) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");

      const config = {
        method,
        headers: {
          "Content-Type": "application/json",
          // Don't send expired tokens on login requests
          ...(token && !isLoginEndpoint(endpoint) && { Authorization: `Bearer ${token}` }),
        },
      };

      if (bodyData) {
        config.body = JSON.stringify(bodyData);
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

      // 🔐 TOKEN EXPIRED – redirect only for protected APIs, NOT for login request
      if (response.status === 401 || response.status === 403) {
        if (!isLoginEndpoint(endpoint)) {
          localStorage.removeItem("token");
          sessionStorage.removeItem("token");
          // Only redirect if not already on login page (prevents Safari reload loop)
          if (!window.location.pathname.includes("/login")) {
            Toast.error("Session expired. Please login again.");
            window.location.href = "/login";
          }
        }
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody?.message || errBody?.detail || "Unauthorized");
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || err?.detail || "Request failed");
      }

      const result = await response.json();
      setData(result);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { data, error, execute };
};
